import React from 'react'
import {
  Table,
  Checkbox,
  Typography,
  Button,
  Input,
  Radio,
  Space,
  Modal,
  message,
  Alert,
  Result
} from 'antd'
import { SearchOutlined, FilterFilled, PlusOutlined, DeleteOutlined, ReloadOutlined } from '@ant-design/icons'
import { ColumnsType } from 'antd/es/table'
import debounce from 'lodash.debounce'
import { MainTitle } from 'components/ui'
import { productApi } from 'api/product'
import { seoPageApi } from 'api/seoPages'
import { ISeoPage, ESeoPageType } from 'models/seoPages'
import { showServerError } from 'shared/utils/showServerError'
import { DEBOUNCE_DELAY } from 'shared/constants/app'
import { useIsMobile } from 'shared/hooks/useIsMobile'

interface ISeoPagesList {
  onCreateClick?: () => void
  onEditClick?: (id: string) => void
}

export const SeoPagesList: React.FC<ISeoPagesList> = React.memo(({ onCreateClick, onEditClick }) => {
  const isMobile = useIsMobile()
  const [loading, setLoading] = React.useState(false)
  const [pages, setPages] = React.useState<ISeoPage[]>([])
  const [selectedIds, setSelectedIds] = React.useState<string[]>([])
  const [currentPage, setCurrentPage] = React.useState(1)
  const [total, setTotal] = React.useState(0)
  const [searchName, setSearchName] = React.useState('')
  const [searchUrl, setSearchUrl] = React.useState('')
  const [filterType, setFilterType] = React.useState<ESeoPageType | undefined>(undefined)
  const [deleteModalVisible, setDeleteModalVisible] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)
  const [subCategoryNames, setSubCategoryNames] = React.useState<Map<string, string>>(new Map())

  const pageSize = 30

  const loadPages = async (
    page: number = 1,
    name?: string,
    url?: string,
    type?: ESeoPageType
  ) => {
    try {
      setLoading(true)
      setError(null)
      const skip = (page - 1) * pageSize
      const response = await seoPageApi.getSeoPages({
        skip,
        limit: pageSize,
        name,
        url,
        type
      })
      setPages(response.data.seoPages)
      setTotal(response.data.count)
    } catch (error) {
      setError('Не удалось загрузить список страниц')
      showServerError(error)
      setPages([])
      setTotal(0)
    } finally {
      setLoading(false)
    }
  }

  React.useEffect(() => {
    loadPages()
  }, [])

  React.useEffect(() => {
    productApi
      .getAllSubCategories(0, 1000)
      .then(({ data }) => {
        setSubCategoryNames(new Map(data.map(({ _id, name }): [string, string] => [_id, name])))
      })
      .catch(showServerError)
  }, [])

  const handleSearch = (name: string) => {
    setCurrentPage(1)
    loadPages(1, name, searchUrl, filterType)
  }

  const handleUrlSearch = (url: string) => {
    setCurrentPage(1)
    loadPages(1, searchName, url, filterType)
  }

  const handleTypeFilter = (type: ESeoPageType | undefined) => {
    setCurrentPage(1)
    setFilterType(type)
    loadPages(1, searchName, searchUrl, type)
  }

  const debouncedSearch = React.useCallback(
    debounce(handleSearch, DEBOUNCE_DELAY),
    [searchUrl, filterType]
  )

  const debouncedUrlSearch = React.useCallback(
    debounce(handleUrlSearch, DEBOUNCE_DELAY),
    [searchName, filterType]
  )

  // Cleanup debounced functions on unmount
  React.useEffect(() => {
    return () => {
      debouncedSearch.cancel()
      debouncedUrlSearch.cancel()
    }
  }, [debouncedSearch, debouncedUrlSearch])

  const handlePageChange = (page: number) => {
    setCurrentPage(page)
    loadPages(page, searchName, searchUrl, filterType)
  }



  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedIds(pages.map(page => page._id))
    } else {
      setSelectedIds([])
    }
  }

  const handleDelete = async () => {
    try {
      setLoading(true)
      await seoPageApi.deleteSeoPages(selectedIds)
      message.success('Страницы успешно удалены')
      setSelectedIds([])
      setDeleteModalVisible(false)
      await loadPages(currentPage, searchName, searchUrl, filterType)
    } catch (error) {
      showServerError(error)
      message.error('Не удалось удалить страницы')
    } finally {
      setLoading(false)
    }
  }

  const getTypeLabel = (type: ESeoPageType) => {
    switch (type) {
    case ESeoPageType.SEARCH:
      return 'Поиск'
    case ESeoPageType.FILTER:
      return 'Фильтр'
    default:
      return type
    }
  }

  const columns: ColumnsType<ISeoPage> = [
    {
      title: (
        <Checkbox
          checked={selectedIds.length === pages.length && pages.length > 0}
          indeterminate={selectedIds.length > 0 && selectedIds.length < pages.length}
          onChange={(e) => handleSelectAll(e.target.checked)}
        />
      ),
      dataIndex: 'checkbox',
      width: 48,
      render: (_, record) => (
        <Checkbox
          checked={selectedIds.includes(record._id)}
          onChange={(e) => {
            if (e.target.checked) {
              setSelectedIds([...selectedIds, record._id])
            } else {
              setSelectedIds(selectedIds.filter(id => id !== record._id))
            }
          }}
        />
      )
    },
    {
      title: 'Название',
      dataIndex: 'name',
      width: '25%',
      render: (name) => (
        <Typography.Paragraph ellipsis={{ rows: 2 }} style={{ marginBottom: 0 }}>
          {name}
        </Typography.Paragraph>
      ),
      filterDropdown: ({ close }) => (
        <div className='filterWrapper' onKeyDown={(e) => e.stopPropagation()}>
          <Input
            value={searchName}
            onChange={(e) => {
              setSearchName(e.target.value)
              debouncedSearch(e.target.value)
            }}
            onPressEnter={() => {
              handleSearch(searchName)
              close()
            }}
            placeholder='Поиск по названию'
            className='filterInput'
          />
          <div className='filterFooterButtons'>
            <Button
              type='primary'
              onClick={() => {
                handleSearch(searchName)
                close()
              }}
              icon={<SearchOutlined />}
              size='small'
              className='filterButton'
            >
              Найти
            </Button>
            <Button
              onClick={() => {
                setSearchName('')
                handleSearch('')
                close()
              }}
              size='small'
              className='filterButton'
            >
              Очистить
            </Button>
            <Button
              type='link'
              size='small'
              onClick={() => close()}
            >
              Закрыть
            </Button>
          </div>
        </div>
      ),
      filterIcon: () => (
        <SearchOutlined
          style={{
            color: searchName ? '#1677ff' : 'rgba(0, 0, 0, 0.29)'
          }}
        />
      )
    },
    {
      title: 'Тип',
      dataIndex: 'type',
      width: 110,
      render: (type) => (
        <Typography.Text>{getTypeLabel(type)}</Typography.Text>
      ),
      filterDropdown: ({ close }) => (
        <div className='filterWrapper' onKeyDown={(e) => e.stopPropagation()}>
          <Radio.Group
            onChange={(e) => {
              handleTypeFilter(e.target.value)
              close()
            }}
            value={filterType}
            style={{ display: 'block', marginBottom: 15 }}
          >
            <Space direction='vertical' size={15}>
              <Radio value={ESeoPageType.SEARCH}>Поиск</Radio>
              <Radio value={ESeoPageType.FILTER}>Фильтр</Radio>
            </Space>
          </Radio.Group>
          <Button
            onClick={() => {
              setFilterType(undefined)
              handleTypeFilter(undefined)
              close()
            }}
            size='small'
            className='filterButton'
          >
            Очистить
          </Button>
          <Button
            type='link'
            size='small'
            onClick={() => close()}
          >
            Закрыть
          </Button>
        </div>
      ),
      filterIcon: () => (
        <FilterFilled
          style={{
            color: filterType ? '#1677ff' : 'rgba(0, 0, 0, 0.25)'
          }}
        />
      )
    },
    {
      title: 'Подкатегория',
      dataIndex: 'subCategory_id',
      width: 150,
      render: (subCategoryId) => (
        <Typography.Text style={{ opacity: subCategoryId ? 1 : 0.5 }}>
          {subCategoryId ? (subCategoryNames.get(subCategoryId) ?? subCategoryId) : 'Не указана'}
        </Typography.Text>
      )
    },
    {
      title: 'URL',
      dataIndex: 'url',
      render: (url) => (
        <Typography.Paragraph ellipsis={{ rows: 2 }} style={{ marginBottom: 0 }}>
          {url}
        </Typography.Paragraph>
      ),
      filterDropdown: ({ close }) => (
        <div className='filterWrapper' onKeyDown={(e) => e.stopPropagation()}>
          <Input
            value={searchUrl}
            onChange={(e) => {
              setSearchUrl(e.target.value)
              debouncedUrlSearch(e.target.value)
            }}
            onPressEnter={() => {
              handleUrlSearch(searchUrl)
              close()
            }}
            placeholder='Поиск по URL'
            className='filterInput'
          />
          <div className='filterFooterButtons'>
            <Button
              type='primary'
              onClick={() => {
                handleUrlSearch(searchUrl)
                close()
              }}
              icon={<SearchOutlined />}
              size='small'
              className='filterButton'
            >
              Найти
            </Button>
            <Button
              onClick={() => {
                setSearchUrl('')
                handleUrlSearch('')
                close()
              }}
              size='small'
              className='filterButton'
            >
              Очистить
            </Button>
            <Button
              type='link'
              size='small'
              onClick={() => close()}
            >
              Закрыть
            </Button>
          </div>
        </div>
      ),
      filterIcon: () => (
        <SearchOutlined
          style={{
            color: searchUrl ? '#1677ff' : 'rgba(0, 0, 0, 0.29)'
          }}
        />
      )
    }
  ]

  // Error fallback state
  if (error && !loading && pages.length === 0) {
    return (
      <div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
          <MainTitle text='Страницы' hideOnMobile />
          <Button
            type='primary'
            icon={<PlusOutlined />}
            onClick={onCreateClick}
            disabled
          >
            Создать страницу
          </Button>
        </div>

        <Result
          status='error'
          title='Ошибка загрузки данных'
          subTitle={error}
          extra={
            <Button
              type='primary'
              icon={<ReloadOutlined />}
              onClick={() => loadPages(currentPage, searchName, searchUrl, filterType)}
            >
              Попробовать снова
            </Button>
          }
        />
      </div>
    )
  }

  return (
    <div>
      <div style={{ display: 'flex'}}>
        <MainTitle text='Страницы' hideOnMobile />
        <Button
          type='primary'
          icon={<PlusOutlined />}
          onClick={onCreateClick}
          style={{marginLeft: '10px'}}
        >
        </Button>
      </div>

      {error && pages.length > 0 && (
        <Alert
          message='Предупреждение'
          description={error}
          type='warning'
          showIcon
          closable
          style={{ marginBottom: 16 }}
          onClose={() => setError(null)}
        />
      )}

      {selectedIds.length > 0 && (
        <div style={{ marginBottom: 16 }}>
          <Button
            danger
            icon={<DeleteOutlined />}
            onClick={() => setDeleteModalVisible(true)}
          >
            Действия: удалить ({selectedIds.length})
          </Button>
        </div>
      )}

      <Table
        columns={columns}
        dataSource={pages}
        loading={loading}
        rowKey={(record) => record._id}
        rowClassName='tableRow'
        onRow={(record) => ({
          onClick: (event) => {
            // Не переходить к редактированию если кликнули на чекбокс
            const target = event.target as HTMLElement
            if (target.closest('.ant-checkbox-wrapper') || target.closest('input[type="checkbox"]')) {
              return
            }
            onEditClick?.(record._id)
          },
          style: { cursor: 'pointer' }
        })}
        pagination={{
          current: currentPage,
          pageSize,
          total,
          onChange: handlePageChange,
          showSizeChanger: false
        }}
        tableLayout='fixed'
        scroll={isMobile ? { x: 800 } : undefined}
      />

      <Modal
        title='Подтверждение удаления'
        open={deleteModalVisible}
        onOk={handleDelete}
        onCancel={() => setDeleteModalVisible(false)}
        okText='Удалить'
        cancelText='Отмена'
        okButtonProps={{ danger: true, loading: loading }}
        cancelButtonProps={{ disabled: loading }}
        closable={!loading}
        maskClosable={!loading}
      >
        <p>Вы уверены, что хотите удалить выбранные страницы ({selectedIds.length})?</p>
        <p>Это действие нельзя отменить.</p>
        {loading && <p style={{ color: '#1677ff' }}>Удаление в процессе...</p>}
      </Modal>
    </div>
  )
})